import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import CustomAuth from './pages/CustomAuth';
import Callback from './pages/Callback';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import Reminders from './pages/Reminders';
import Calendar from './pages/Calendar';
import Conversations from './pages/Conversations';
import Chat from './pages/Chat';
import Configuration from './pages/ConfigurationNew';
import Onboarding from './pages/Onboarding';
import AdminPanel from './pages/AdminPanel';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import apiClient, { setSubscriptionErrorCallback } from './utils/api';
import { trackPageView } from './utils/analytics';
import SubscriptionBanner from './components/SubscriptionBanner';
import './App.css';

function ProtectedRoute({ children }) {
  const { token, loading: authLoading } = useAuth();
  const location = useLocation();
  const [needsOnboarding, setNeedsOnboarding] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);

  console.log('ProtectedRoute render:', { 
    pathname: location.pathname, 
    token: !!token, 
    authLoading,
    needsOnboarding, 
    isLoading 
  });

  useEffect(() => {
    // Register callback for 402 errors
    setSubscriptionErrorCallback(setShowSubscriptionBanner);
    
    if (token) {
      checkUserSetup();
    } else if (!authLoading) {
      setIsLoading(false);
    }
  }, [token, authLoading]);

  const checkUserSetup = async () => {
    try {
      const setupResponse = await apiClient.get('/user/setup-status');
      console.log('Setup response:', setupResponse.data);
      setNeedsOnboarding(!setupResponse.data.has_platform_setup);
      
      // Store subscription data if available
      if (setupResponse.data.subscription) {
        console.log('Setting subscription data:', setupResponse.data.subscription);
        setSubscriptionData(setupResponse.data.subscription);
      } else {
        console.log('No subscription data in response');
      }
    } catch (error) {
      console.error('Failed to check user setup:', error);
      setNeedsOnboarding(false);
      // Show banner on 402 error
      if (error.response?.status === 402) {
        setShowSubscriptionBanner(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnboardingComplete = () => {
    setNeedsOnboarding(false);
  };

  // Wait for auth to finish loading
  if (authLoading) {
    console.log('Auth loading...');
    return <div>Loading...</div>;
  }

  if (!token) {
    console.log('No token, redirecting to login');
    return <Navigate to={`/auth${location.search}`} state={{ from: location }} replace />;
  }

  if (isLoading) {
    console.log('Loading...');
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (needsOnboarding) {
    console.log('Needs onboarding, showing onboarding component');
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  console.log('Rendering children for:', location.pathname);
  return (
    <>
      <SubscriptionBanner subscriptionData={subscriptionData} />
      {children}
    </>
  );
}

// Component to track page views on route changes
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    const pageName = location.pathname === '/' ? 'Dashboard' : 
                    location.pathname.substring(1).charAt(0).toUpperCase() + 
                    location.pathname.substring(2);
    trackPageView(pageName);
  }, [location]);
  
  return null;
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <AnalyticsTracker />
          <div className="App">
            <Routes>
              <Route path="/auth" element={<CustomAuth />} />
              <Route path="/abc" element={<Onboarding />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/callback" element={<Callback />} />
              <Route path="/auth/callback.html" element={<AuthCallback />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/tasks" element={
                <ProtectedRoute>
                  <Tasks />
                </ProtectedRoute>
              } />
              <Route path="/reminders" element={
                <ProtectedRoute>
                  <Reminders />
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              } />
              <Route path="/conversations" element={
                <ProtectedRoute>
                  <Conversations />
                </ProtectedRoute>
              } />
              <Route path="/chat" element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              } />
              <Route path="/config" element={
                <ProtectedRoute>
                  <Configuration />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              } />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;